/**
 * Rummy 500 bot.
 *
 * Plays from its own view only:
 *  - Draw: takes a card from the discard pile when the rules allow it (the card must be usable at once), choosing the
 *    deepest pick that wins the most points; otherwise draws from the stock.
 *  - Play: first uses a card taken from the pile, then lays down every set and run it holds (highest points first),
 *    lays off singles on the table melds, and finally discards the highest-value card that does not help a meld.
 */
import { parseCard, type CardId } from '../../core/cards.js';
import { chance, type BotEngine } from '../../bots/types.js';
import {
  rummy500FindUse,
  rummy500HandValue,
  rummy500LayoffSpot,
  type Rummy500Action,
  type Rummy500Legal,
  type Rummy500View,
} from './engine.js';

const rankOf = (id: CardId) => parseCard(id).rank;
const suitOf = (id: CardId) => parseCard(id).suit;

/** The most valuable new meld in `hand`, or null. Sets and the longest runs are considered. */
function bestMeld(hand: readonly CardId[]): CardId[] | null {
  const options: CardId[][] = [];
  const byRank = new Map<number, Map<string, CardId>>();
  for (const c of hand) {
    const m = byRank.get(rankOf(c)) ?? new Map<string, CardId>();
    m.set(suitOf(c), c);
    byRank.set(rankOf(c), m);
  }
  for (const m of byRank.values()) if (m.size >= 3) options.push([...m.values()]);
  for (const suit of ['S', 'H', 'D', 'C']) {
    const mine = hand.filter((c) => suitOf(c) === suit);
    for (const aceLow of [true, false]) {
      const byPos = new Map<number, CardId>();
      for (const c of mine) byPos.set(rankOf(c) === 14 && aceLow ? 1 : rankOf(c), c);
      let run: CardId[] = [];
      for (let p = 1; p <= 14; p++) {
        const c = byPos.get(p);
        if (c) run.push(c);
        else {
          if (run.length >= 3) options.push(run);
          run = [];
        }
      }
      if (run.length >= 3) options.push(run);
    }
  }
  if (options.length === 0) return null;
  const worth = (cs: CardId[]) => cs.reduce((n, c) => n + rummy500HandValue(c), 0) + cs.length;
  return options.sort((a, b) => worth(b) - worth(a))[0]!;
}

/** How much a card helps the hand: neighbours of the same rank or the same suit one or two steps away. */
function usefulness(card: CardId, hand: readonly CardId[]): number {
  let n = 0;
  for (const o of hand) {
    if (o === card) continue;
    if (rankOf(o) === rankOf(card)) n += 2;
    else if (suitOf(o) === suitOf(card)) {
      const d = Math.abs(rankOf(o) - rankOf(card));
      const ace = (rankOf(o) === 14 && rankOf(card) <= 3) || (rankOf(card) === 14 && rankOf(o) <= 3);
      if (d === 1 || ace) n += 2;
      else if (d === 2) n += 1;
    }
  }
  return n;
}

function chooseDiscard(view: Rummy500View, cards: readonly CardId[]): CardId {
  const cost = (c: CardId) => rummy500HandValue(c) - usefulness(c, view.hand) * 6;
  return [...cards].sort((a, b) => cost(b) - cost(a))[0]!;
}

function choosePlay(view: Rummy500View, legal: Extract<Rummy500Legal, { kind: 'play' }>, mistake: boolean): Rummy500Action {
  if (legal.mustUse) {
    const use = rummy500FindUse(legal.mustUse, view.hand, view.melds);
    if (use) return use;
  }
  if (!mistake) {
    const meld = bestMeld(view.hand);
    if (meld) return { type: 'meld', cards: meld };
    let best: { meldId: number; card: CardId } | null = null;
    for (const c of view.hand) {
      for (const m of view.melds) {
        if (rummy500LayoffSpot(m, c) !== null && (!best || rummy500HandValue(c) > rummy500HandValue(best.card))) best = { meldId: m.id, card: c };
      }
    }
    if (best) return { type: 'layoff', ...best };
  }
  return { type: 'discard', card: chooseDiscard(view, legal.discard) };
}

export const rummy500Bot: BotEngine<Rummy500View, Rummy500Legal, Rummy500Action> = {
  decideAction(view, legal, ctx) {
    return this.chooseCard!(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind === 'draw') {
      if (legal.takeable.length > 0 && !chance(ctx, ctx.profile.mistakeRate)) {
        // Deepest takeable card = the most cards (and points) won.
        const pick = legal.takeable[0]!;
        const at = view.pile.indexOf(pick);
        const gain = view.pile.slice(at).reduce((n, c) => n + rummy500HandValue(c), 0);
        if (gain >= 8 || chance(ctx, 0.5)) return { type: 'takePile', card: pick };
      }
      return { type: 'draw' };
    }
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    return choosePlay(view, legal, chance(ctx, ctx.profile.mistakeRate));
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const best = Math.max(...view.scores);
    return Math.max(0, Math.min(1, 0.5 + (view.scores[me]! - best) / 1000));
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'draw') return { type: 'draw' };
    if (legal.kind === 'play') return choosePlay(view, legal, false);
    throw new Error('No legal action');
  },
};
